import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { RbacFormField } from '../../../../shared/component/rbac-form/rbac-form-field.model';
import { RbacFormComponent } from '../../../../shared/component/rbac-form/rbac-form.component';
import { RbacModalComponent } from '../../../../shared/component/rbac-modal/rbac-modal.component';
import { RbacTableColumn } from '../../../../shared/component/rbac-table/rbac-table-column.model';
import { RbacTableComponent } from '../../../../shared/component/rbac-table/rbac-table.component';
import { Module, SubModule, HIERARCHICAL_ROUTES, findRouteByPath } from '../rbac.models';
import { ApiCallsService } from '../../../../shared/services/api-calls.service';
import { ToastService } from '../../../../shared/services/toast.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-submodule-management',
  standalone: true,
  imports: [CommonModule, RbacTableComponent, RbacFormComponent, RbacModalComponent],
  templateUrl: './submodule-management.component.html',
  styleUrls: ['./submodule-management.component.scss']
})
export class SubModuleManagementComponent implements OnChanges {
  // The parent module these sub-modules belong to (set by the shell
  // when the user clicks a module row in ModuleManagementComponent).
  @Input({ required: true }) module!: Module;
  @Output() back = new EventEmitter<void>();
  @Output() manageInnerModules = new EventEmitter<SubModule>();

  subModules: SubModule[] = [];
  loading = false;
  saving = false;
  modalOpen = false;
  editingSubModule: SubModule | null = null;

  columns: RbacTableColumn[] = [
    { key: 'name', label: 'Sub-Module Name' },
    { key: 'url', label: 'URL' },
    { key: 'api_url', label: 'API URL' },
    { key: 'category', label: 'Category' },
    { key: 'order', label: 'Order', width: '80px' },
    { key: 'is_view', label: 'Read', type: 'badge-yn' },
    { key: 'is_add', label: 'Add', type: 'badge-yn' },
    { key: 'is_edit', label: 'Edit', type: 'badge-yn' },
    { key: 'sub_module', label: 'Has Inner-Modules', type: 'badge-yn' },
    { key: 'status', label: 'Status', type: 'status' }
  ];

  fields: RbacFormField[] = [];

  constructor(private configurationService: ApiCallsService, private toast: ToastService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['module'] && this.module) {
      this.buildFields();
      this.loadSubModules();

    }
  }

  private buildFields(): void {
    const parentRoute = findRouteByPath(HIERARCHICAL_ROUTES, this.module?.url || '');
    const subRouteOptions = parentRoute && parentRoute.children
      ? parentRoute.children.map(c => ({ label: c.label, value: c.path }))
      : [];

    this.fields = [
      { key: 'name', label: 'Sub-Module Name', type: 'text', required: true, placeholder: 'e.g. Daily Report' },
      { key: 'url', label: 'URL', type: 'select', placeholder: 'Select URL...', options: subRouteOptions },
      { key: 'order', label: 'Order', type: 'number', required: true },
      { key: 'parent_id', label: 'Module', type: 'hidden' },
      { key: 'is_view', label: 'Can View?', type: 'toggle' },
      { key: 'is_add', label: 'Can Add?', type: 'toggle' },
      { key: 'is_edit', label: 'Can Edit?', type: 'toggle' },
      { key: 'sub_module', label: 'Has Inner-Modules?', type: 'toggle' },
      {
        key: 'api_url', label: 'API URL',
        type: 'text'
      },
      {
        key: 'category', label: 'Category', type: 'select', placeholder: 'Select category types...', options: []
      }
    ];
  }

  loadSubModules(): void {
    this.loading = true;
    this.configurationService
      .get({ type: 'S', parent_id: this.module._id! })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          const all = res?.Data ?? res ?? [];
          this.subModules = all.filter((row: any) => row.type === 'S' && row.parent_id === this.module._id);
        },
        error: () => (this.subModules = [])
      });
    this.loadCategories()
  }
  private loadCategories(): void {
    this.configurationService.getCategories().subscribe({
      next: (res) => {
        const categories = res?.Data ?? res ?? [];
        const categoryField = this.fields.find(f => f.key === 'category');
        if (categoryField) {
          categoryField.options = categories.map((c: any) => ({
            label: c.name ?? c.label,
            value: c.category_type ?? c.category
          }));
          this.fields = [...this.fields]; // new array reference so the form re-renders if it's already open
        }
      }
    });
  }
  openAdd(): void {
    this.editingSubModule = { module_id: this.module._id } as SubModule;
    this.modalOpen = true;
  }

  openEdit(subModule: SubModule): void {
    this.editingSubModule = subModule;
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
    this.editingSubModule = null;
  }

  onSave(value: Record<string, any>): void {
    this.saving = true;
    const { _id, ...rest } = value;
    const payload: any = { ...rest, order: Number(value['order']), type: 'S', parent_id: this.module._id };

    const request$ = _id
      ? this.configurationService.update({ id: _id, ...payload })
      : this.configurationService.add(payload as SubModule);

    const action = _id ? 'Updated' : 'Added';

    request$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: (res: any) => {
        if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
          const errorMsg = res?.Message || res?.message || `Failed to ${action.toLowerCase()} Submodule`;
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
          return;
        }
        this.toast.showToast(`Submodule ${action} Successfully`, 'colored', 5000, 'success');
        this.closeModal();
        this.loadSubModules();
      },
      error: err => {
        const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || `Failed to ${action.toLowerCase()} Submodule`;
        this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
      }
    });
  }
  onToggleStatus(subModule: SubModule): void {
    if (!subModule.id) { return; }
    const nextStatus = subModule.status === 'N' ? 'Y' : 'N';
    this.configurationService
      .update({
        id: subModule.id,
        status: nextStatus,
        order: subModule.order,
        is_add: subModule.is_add,
        is_view: subModule.is_view,
        is_edit: subModule.is_edit,
        type: 'S',
        parent_id: this.module._id
      })
      .subscribe({
        next: (res: any) => {
          if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
            const errorMsg = res?.Message || res?.message || 'Failed to update status';
            this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
            return;
          }
          this.loadSubModules();
        },
        error: err => {
          const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || 'Failed to update status';
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
        }
      });
  }

  onDelete(subModule: SubModule): void {
    const id = subModule._id || subModule.id;
    if (!id) { return; }
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you really want to delete sub-module "${subModule.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'No, cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6'
    }).then((result) => {
      if (!result.isConfirmed) {
        this.toast.showToast('Action cancelled', 'colored', 3000, 'info');
        return;
      }
      this.configurationService
        .update({
          id,
          ...subModule,
          status: 'N',
          order: Number(subModule.order),
          type: 'S',
          parent_id: this.module._id
        })
        .subscribe({
          next: (res: any) => {
            if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
              const errorMsg = res?.Message || res?.message || 'Failed to delete sub-module';
              this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
              return;
            }
            this.toast.showToast('Sub-module deleted successfully', 'colored', 5000, 'success');
            this.loadSubModules();
          },
          error: err => {
            const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || 'Failed to delete sub-module';
            this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
          }
        });
    });
  }
}
